import React from 'react';

import { type UUID, calcValue, isDefined, isTrue, noop, randomIntBetween } from '@nfctron/nexus-core';

import { useInterval } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@root/features/api-client/api-client';
import { useVisualEffects } from '@root/features/layout/components/VisualEffects/VisualEffects';

/**
 * main-config-store schema
 * @see https://vercel.com/nfctron/nfctron-tickets/stores/edge-config/ecfg_snz1szzi69wradfsm02ywaibirch/items
 */
const storeSchema = z.object({
	digest: z.string(),
	updatedAt: z.number(),
	items: z.object({
		tickets_cartQueued: z.array(
			z.object({
				matchByOrgSlug: z.string().optional(),
				matchByEventId: z.string().optional(),
				hitRate: z.number().optional().default(100),
				queueLimit: z.number().optional(),
			}),
		),
	}),
});

// TODO: make interval and enabled from the store config (fetched at getStatticProps)
export const ENTRY_QUEUE_INTERVAL = Number(process.env.NEXT_PUBLIC_EVENT_ENTRY_QUEUE_INTERVAL) || 20_000;

export const getUseEntryQueueKey = (eventId: UUID, organizerSlug: string) => ['entryQueue', eventId, organizerSlug] as const;

export function useEntryQueue(options: { eventId: UUID; organizerSlug: string; isTicketSaleEnabled: boolean; queueLimit?: number }) {
	const ENTRY_QUEUE_ENABLED = isTrue(process.env.NEXT_PUBLIC_EVENT_ENTRY_QUEUE_ENABLED || false);
	const [_currentPlace, setCurrentPlace] = React.useState<number>(0);

	const entryQueueApi = useQuery({
		queryKey: getUseEntryQueueKey(options.eventId, options.organizerSlug),
		queryFn: async ({ queryKey }) => {
			const [_, eventId, organizerSlug] = queryKey;
			try {
				/** get the store config from vercel-edge */
				const res = await apiClient.get(`ecfg_snz1szzi69wradfsm02ywaibirch?token=84a03c3c-36ef-4a24-bd51-21cd872c29d4`, {
					baseURL: 'https://edge-config.vercel.com',
				});

				/** parse the store config */
				const store = storeSchema.parse(res.data);

				/** get config for the given event (organizer identified) */
				const queueConfig = store.items.tickets_cartQueued.find((item) => {
					let isMatch = false;
					/** match by event slug if defined */
					if (isDefined(item.matchByEventId)) isMatch ||= item.matchByEventId === eventId;
					/** match by org slug if defined */
					if (isDefined(item.matchByOrgSlug)) isMatch ||= item.matchByOrgSlug === organizerSlug;
					return isMatch;
				});

				/** no config defined, pass */
				if (!isDefined(queueConfig)) return { shouldQueue: false, queueConfig, store };

				/** hitRate represents the percentage (0-100) of users that should be kept in the queue */
				const hitRate = queueConfig.hitRate;

				/** get random value as users chance to be in the queue */
				const randomValue = randomIntBetween(0, 100);

				/** should user be in the queue if he hits lower than the hitRate */
				const shouldQueue = randomValue < hitRate;

				/** get the current place */
				let currentPlace = _currentPlace;
				/** if initial, try to get from the queueLimit store config */
				if (currentPlace === 0 && isDefined(queueConfig.queueLimit)) {
					/** add random +-10% to the queueLimit to ensure true randomness */
					currentPlace = randomIntBetween(queueConfig.queueLimit / 1.1, queueConfig.queueLimit * 1.1);
				}

				/** if still initial, generate random value in the 90th percentile of the event queueLimit config */
				if (currentPlace === 0 && isDefined(options.queueLimit)) {
					currentPlace = randomIntBetween(options.queueLimit / 1.1, options.queueLimit);
				}

				/** calculate new current place (quantify random value as percentage of current place, to make randomly lower value) */
				const changeQuantifier = 1 - hitRate / 100;
				let newCurrentPlace = Math.ceil(currentPlace - currentPlace * changeQuantifier || 0);
				/** however if the hit-rate is still 100% (full stop) then keep the current place */
				if (hitRate === 100) newCurrentPlace = currentPlace;
				/** also do not allow to go below 9th place */
				if (newCurrentPlace < 9) newCurrentPlace = 9;

				return {
					shouldQueue,
					lastCheck: new Date(),
					nextCheck: new Date(Date.now() + ENTRY_QUEUE_INTERVAL),
					randomValue,
					currentPlace: newCurrentPlace > 0 ? newCurrentPlace : null,
					queueConfig,
					store,
				};
			} catch (err) {
				console.warn('Entry queue is not available', err);
				return { shouldQueue: false };
			}
		},
		enabled: ENTRY_QUEUE_ENABLED && options.isTicketSaleEnabled,
		retry: 0,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	const canContinueFetching = calcValue(() => {
		/** pass through if the entry queue is not enabled */
		if (!ENTRY_QUEUE_ENABLED) return options.isTicketSaleEnabled;
		/** if it is enabled, check for data and if the user should not be in the queue */
		return isDefined(entryQueueApi.data) && !entryQueueApi.data.shouldQueue;
	});
	const isEntryQueueActive = isDefined(entryQueueApi.data) && entryQueueApi.data.shouldQueue;

	React.useEffect(() => {
		if (isDefined(entryQueueApi.data?.currentPlace)) {
			setCurrentPlace(entryQueueApi.data.currentPlace);
		}
	}, [entryQueueApi.data?.currentPlace]);

	const interval = useInterval(() => {
		entryQueueApi.refetch().catch(noop);
	}, ENTRY_QUEUE_INTERVAL);

	const confetti = useVisualEffects();

	/** start/stop the refetch interval */
	React.useEffect(() => {
		if (isDefined(entryQueueApi.data)) {
			if (!entryQueueApi.data.shouldQueue && interval.active) {
				confetti.confettiShootDefault();
				interval.stop();
			} else if (entryQueueApi.data.shouldQueue && !interval.active) {
				interval.start();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entryQueueApi.data, entryQueueApi.data?.shouldQueue]);

	return {
		entryQueueApi,
		canContinueFetching,
		isEntryQueueActive,
	};
}
